// Masked custom domains: a marketplace store served on its own domain
// (e.g. aregomd.com) instead of office.aregobrands.com/marketplace/aregomd.
//
// The office app and every store are the SAME SPA bundle. When the bundle is
// served on a domain that is NOT one of the app's own hosts, we ask the API
// (resolve-domain, by Host header) which store that domain maps to, then render
// that storefront at clean root paths (aregomd.com/hrt, aregomd.com/shop).
//
// API calls always go to the absolute apiBaseUrl (injected via __APP_CONFIG__),
// so a masked domain reaches the real API cross-origin — CORS is "*".

import { apiBaseUrl } from "../config/runtimeConfig";

// Hosts that ARE the back-office app (never a masked store). Everything else is
// treated as a candidate masked domain and resolved against the API.
const APP_HOSTS = [
  "office.aregobrands.com",
  "office2.aregobrands.com",
  "plan-x-mlm-frontend.zzvi8n.easypanel.host",
  "localhost",
  "127.0.0.1",
  "",
];

export function isAppHost(hostname = window.location.hostname) {
  const h = String(hostname || "").toLowerCase();
  return APP_HOSTS.includes(h) || h.endsWith(".easypanel.host");
}

// Could this host be a masked store domain? (Sync, hostname-only — used to
// decide whether to even attempt resolution.)
export function isMaskedHost(hostname = window.location.hostname) {
  return !isAppHost(hostname);
}

let _slug = null; // resolved masked store slug, or null
let _public = false; // is that store a public storefront?
let _template = ""; // its template id, when the server told us up front
let _resolved = false; // resolution attempted this page-load
let _inflight = null; // de-dupes concurrent callers

// Resolve the current host to a store slug. Safe to call repeatedly; only the
// first call hits the network. Returns the slug (or null on any failure — the
// app then falls back to normal office behaviour).
export function resolveMaskedDomain() {
  if (_resolved) return Promise.resolve(_slug);
  if (_inflight) return _inflight;
  if (isAppHost()) {
    _resolved = true;
    _slug = null;
    return Promise.resolve(null);
  }
  // The server already answered, in the head (seoBootScript in seorender.go):
  // nginx resolves the host on its way past, so on a store's own domain this
  // is known before a single byte of the bundle has run. main.jsx waits on
  // this promise before the first render, so fetching it instead put a whole
  // round trip in front of the first pixel of every cold visit. The fetch
  // below stays as the fallback for a stale shell or an SSI miss.
  const boot = typeof window !== "undefined" ? window.__AREGO_STORE__ : null;
  if (boot && boot.slug) {
    _slug = String(boot.slug);
    _public = boot.public === true;
    _template = boot.template == null ? "" : String(boot.template);
    _resolved = true;
    return Promise.resolve(_slug);
  }
  _inflight = fetch(
    `${apiBaseUrl}/api/v1/public/resolve-domain?host=${encodeURIComponent(window.location.hostname)}`,
  )
    .then((res) => (res.ok ? res.json() : null))
    .then((data) => {
      _slug = data && data.slug ? String(data.slug) : null;
      _public = !!(data && data.public);
      return _slug;
    })
    .catch(() => {
      _slug = null;
      return null;
    })
    .finally(() => {
      _resolved = true;
      _inflight = null;
    });
  return _inflight;
}

export function getMaskedSlug() {
  return _slug;
}

// True only when we're on a masked host AND it resolved to a store.
export function maskedActive() {
  return isMaskedHost() && !!_slug;
}

// Build an internal store URL. On the masked domain of THIS store, links are
// root-relative (/shop, /hrt); everywhere else they keep the /marketplace/:slug
// prefix. `sub` may include a trailing query string ("shop?x=1").
// Is the masked store a public storefront? (false = members-only.)
export function maskedPublic() {
  return _public;
}

// Which template this store renders, when the server said so in the head.
// Empty when it did not — the caller then finds out from the store payload as
// it always did.
export function maskedTemplate() {
  return _template;
}

// The back office. Sign-in and Dashboard always happen HERE, never on a masked
// store domain — a masked domain is only that one storefront.
export const OFFICE_ORIGIN = "https://office.aregobrands.com";

export function officeUrl(path = "/") {
  return OFFICE_ORIGIN + (path.startsWith("/") ? path : "/" + path);
}

// Where the Dashboard button goes. On a masked domain that's the back office
// itself (it routes the signed-in user to their own dashboard); on office we
// keep the direct in-app dashboard path.
export function dashboardUrl(scope) {
  if (maskedActive()) return officeUrl("/");
  return `/customers/${encodeURIComponent(scope)}/dashboard`;
}

// Where "Edit profile" / "add a shipping address" links go from checkout.
// Account/profile is a back-office-only route — a masked store domain (e.g.
// aueraskincare.com) doesn't serve it at all, so a bare relative href there
// resolves to that domain and crashes (generic router error screen) instead
// of showing the customer's profile. Same masked-aware pattern as
// dashboardUrl, just for this specific path instead of "/".
export function accountProfileUrl(scope) {
  const path = `/customers/${encodeURIComponent(scope)}/account/profile`;
  return maskedActive() ? officeUrl(path) : path;
}

// ── Cross-domain session handoff ────────────────────────────────────────────
// The session lives in localStorage, which is per ORIGIN: signing in on
// office.aregobrands.com leaves adaptx1.com still looking logged-out. So after
// an office login that came from a masked store, office hands the session back
// in the URL FRAGMENT (never the query string — fragments aren't sent to the
// server, proxies or Referer headers). We consume it on arrival, persist it for
// this origin, and strip it from the address bar immediately.
// Fragment key carrying a session between origins. The pre-boot script in
// index.html both writes and consumes this — keep the two in sync.
export const HANDOFF_KEY = "arego_session";

// ── Sign-in chain ───────────────────────────────────────────────────────────
// Browser storage is per origin, so "log in once, be signed in on every store"
// means each store domain is visited once carrying the session. Office builds
// that chain right after a login: hop → store A → store B → … → destination,
// each hop answered by /sso-plant.html (nginx routes ?aregoSsoPlant=1 there),
// a ~3 KB page with no bundle. About a second for seven stores, once.
//
// This replaces the check every cold visit used to make: a token-less visitor
// no longer asks office at all, so a first visit never leaves the store domain
// — which was ~4 seconds of the audit's largest-paint time. The chain is keyed
// on the session (arego_planted) so the boot check on office (main.jsx) plants
// exactly once per login, and covers anyone signed in before this shipped.
export const PLANT_PARAM = "aregoSsoPlant";
export const CHAIN_PARAM = "aregoChain";
export const FINAL_PARAM = "final";
const PLANTED_KEY = "arego_planted";

// Stamp = the signed-in USER, not the token. The office token rotates every
// 25 minutes (useToken.jsx refresh), and keying the stamp on it meant every
// full load after a rotation — every deploy's silent reload, every refresh
// half an hour later — walked the browser through all the store domains
// again (2026-10-01 report). Stores re-verify with office on their own full
// loads anyway, so planting once per login is all that is needed.
const sessionStamp = (sessionJson) => {
  try {
    const t = JSON.parse(sessionJson);
    const uid = String(t?.userId ?? "").trim();
    return uid ? `u:${uid}` : "";
  } catch { return ""; }
};

export function plantChainUrl(host, remaining, finalUrl, payload) {
  const u = new URL(`https://${host}/`);
  u.searchParams.set(PLANT_PARAM, "1");
  if (remaining.length) u.searchParams.set(CHAIN_PARAM, remaining.join(","));
  u.searchParams.set(FINAL_PARAM, finalUrl);
  u.hash = `${HANDOFF_KEY}=${payload}`;
  return u.toString();
}

// Office only. Navigates away (returns true) when there is planting to do;
// returns false — and does nothing — when this session is already planted or
// there is no session. `finalUrl` is where the visitor lands afterwards.
// `force` (a real login) plants even when this user was planted before, so
// the stores receive the fresh session; the boot check in main.jsx leaves it
// off and therefore only ever plants a browser that has never been planted.
export function provisionStores(sessionJson, finalUrl, { force = false } = {}) {
  if (typeof window === "undefined" || !sessionJson) return false;
  const stamp = sessionStamp(sessionJson);
  if (!stamp) return false;
  if (!force) {
    try { if (localStorage.getItem(PLANTED_KEY) === stamp) return false; } catch { /* ignore */ }
  }
  const dest = finalUrl || window.location.href;
  const go = (list) => {
    const domains = (Array.isArray(list) ? list : []).map((d) => String(d || "").trim()).filter(Boolean);
    try { localStorage.setItem(PLANTED_KEY, stamp); } catch { /* ignore */ }
    if (!domains.length) { if (finalUrl) window.location.href = finalUrl; return; }
    window.location.replace(plantChainUrl(domains[0], domains.slice(1), dest, encodeSession(sessionJson)));
  };
  const inline = window.__AREGO_MASKED__;
  if (Array.isArray(inline)) { go(inline); return true; }
  fetch(`${apiBaseUrl}/api/v1/public/masked-domains`)
    .then((r) => (r.ok ? r.json() : null))
    .then((d) => go(d && d.domains ? d.domains : []))
    .catch(() => go([]));
  return true;
}

// Forget the stamp on logout so the next login plants again.
export function forgetPlanted() {
  try { localStorage.removeItem(PLANTED_KEY); } catch { /* ignore */ }
}

export function encodeSession(sessionJson) {
  // base64url so the payload survives a URL fragment intact.
  return btoa(unescape(encodeURIComponent(sessionJson)))
    .replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

// ── iPhone Home Screen app ──────────────────────────────────────────────────
// A store added to the Home Screen runs as its own app with its own storage,
// and any page on another domain opens in a separate browser sheet that
// shares none of it. So the office sign-in (and the hop that brings the
// session back) can never reach the app. In the app, Sign in opens a sheet on
// the store's own domain instead (pages/marketplace/appSignIn.jsx), and the
// pre-boot script in index.html skips its office check.
//
// navigator.standalone is set only by iPhone and iPad. Android's installed
// apps share the browser's storage, so the office sign-in works there.
export const APP_SIGNIN_PARAM = "aregoSignIn";

export function homeScreenApp() {
  try { return window.navigator.standalone === true; } catch { return false; }
}

// This page with the sign-in sheet open. `back` is where to land afterwards:
// a page on this domain, otherwise this page.
export function appSignInUrl(back) {
  let target = "1";
  try {
    const b = new URL(back || "", window.location.href);
    if (b.origin === window.location.origin) {
      b.searchParams.delete(APP_SIGNIN_PARAM);
      target = b.pathname + b.search + b.hash;
    }
  } catch { /* stay on this page */ }
  const u = new URL(window.location.href);
  u.searchParams.set(APP_SIGNIN_PARAM, target);
  return u.pathname + u.search;
}

// Where a "Sign in" / auth bounce should send the visitor.
//
// Always the BACK OFFICE — one login for every marketplace, rather than a
// separate account per domain. On office that's "/?returnTo=…" (the app's root
// renders Login for a guest). On a masked domain "/" is the store's own home
// page, so we send an ABSOLUTE office URL and carry an absolute returnTo, and
// office hands the session back to this domain afterwards (adopted by the
// pre-boot script in index.html) so the visitor lands signed in where they
// started. Other stores pick the session up lazily on first visit.
// The one exception is a store's iPhone Home Screen app (see above).
export function loginUrl(returnTo) {
  const rt = returnTo || (window.location.pathname + window.location.search);
  if (maskedActive()) {
    const absolute = rt.startsWith("http") ? rt : window.location.origin + rt;
    if (homeScreenApp()) return appSignInUrl(absolute);
    return `${OFFICE_ORIGIN}/?returnTo=${encodeURIComponent(absolute)}`;
  }
  return `/?returnTo=${encodeURIComponent(rt)}`;
}

export function storeUrl(slug, sub = "") {
  const clean = String(sub || "").replace(/^\/+/, "");
  if (maskedActive() && slug && String(slug).toLowerCase() === String(_slug).toLowerCase()) {
    return "/" + clean;
  }
  return "/marketplace/" + slug + (clean ? "/" + clean : "");
}

// Reduce a user-entered custom domain to a bare host — the JS twin of the
// backend's normalizeMaskedDomain (internal/api/marketplaces.go). Keep the two
// in sync: they have to agree on what "aregomd.com" means.
export function normalizeDomain(v) {
  const s = String(v ?? "").trim().toLowerCase();
  if (!s) return "";
  return s
    .replace(/^[a-z][a-z0-9+.-]*:\/\//, "")
    .replace(/[/?#].*$/, "")
    .replace(/:\d+$/, "")
    .replace(/\.$/, "")
    .replace(/^www\./, "");
}

// The ABSOLUTE, shareable URL for a store — what a member copies and sends to a
// prospect. When the store has a masked custom domain it wins, so the link reads
// aregomd.com/johnd instead of office.aregobrands.com/marketplace/aregomd/johnd.
// (storeUrl above is the opposite job: relative, in-app navigation.)
//
// `sub` is the webAlias for a referral link, "" for a plain store link.
export function publicStoreUrl(mkt, sub = "") {
  const clean = String(sub || "").replace(/^\/+/, "");
  const domain = normalizeDomain(mkt?.maskedDomain);
  // Off a masked host the office is the only base that resolves for anyone else.
  const officeBase = isAppHost() ? window.location.origin : OFFICE_ORIGIN;
  const base = domain ? `https://${domain}` : `${officeBase}/marketplace/${mkt?.slug ?? ""}`;
  return clean ? `${base}/${clean}` : base;
}

// "Open THIS store as ME" — the URL used by Open-full / store-open buttons in
// office. Same as publicStoreUrl, but when the store has a MASKED custom
// domain (a separate origin with its own localStorage), it appends the current
// session as a #arego_session handoff fragment so the store adopts MY session
// on load — the pre-boot SSO script overwrites any stale token there
// unconditionally. Without this, opening the masked domain (with rel=noreferrer)
// keeps whatever account that domain was last logged into. NEVER use this for a
// shareable/copy link (publicStoreUrl) — it would embed the token. Mirrors the
// login.jsx finishLogin handoff, skipping the resolve-domain round-trip since
// mkt.maskedDomain is already the store's own verified domain.
export function openStoreUrl(mkt, sub = "") {
  const url = publicStoreUrl(mkt, sub);
  const domain = normalizeDomain(mkt?.maskedDomain);
  if (!domain) return url; // office-relative / same-origin — no handoff needed
  let session = null;
  try { session = localStorage.getItem("token"); } catch (e) { /* ignore */ }
  if (!session) return url;
  const sep = url.includes("#") ? "&" : "#";
  return `${url}${sep}${HANDOFF_KEY}=${encodeSession(session)}`;
}
